import * as React from "react";
import { Popover } from "radix-ui";
import { Info } from "lucide-react";
import { Eyebrow } from "@ozpanel/ui";

// InfoPopover — a small (i) button that reveals a plain-language explanation on click. For the
// customer consoles, where the MEANING of a control matters (a spam score, a threshold, what an
// action learns) but a permanent paragraph beside every field turns a form into a wall of prose.
//
// Deliberately NOT FieldInfo: that is the daemon-directive reference for the settings engine
// (directive name, type, config file, accepted values from module metadata). This carries any
// human content and knows nothing about settings fields, so it can sit next to a hand-built input.
export function InfoPopover({
  title,
  children,
  side = "top",
}: {
  title?: string;
  children: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={title ? `About ${title}` : "More information"}
          // The glyph stays 15px; the hit area around it is 32px (40 on touch) via the negative margin.
          className="-my-2 -mr-2 inline-grid size-8 place-items-center rounded-full align-middle text-faint transition-colors hover:text-accent pointer-coarse:size-10"
        >
          <Info size={12} />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side={side}
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-50 w-[min(300px,calc(100vw-24px))] rounded-[11px] border border-line bg-surface p-3.5 shadow-[0_18px_50px_-12px_rgba(0,0,0,.55)]"
        >
          {title && (
            <Eyebrow accent className="mb-1.5">
              {title}
            </Eyebrow>
          )}
          <div className="text-[12.5px] leading-relaxed text-muted [&_code]:font-mono [&_strong]:text-fg">{children}</div>
          <Popover.Arrow className="fill-[var(--ozp-line)]" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
